import { useMemo } from 'react';
import type { Appointment, AppointmentType } from '../../../mocks/appointments';
import { statusLabels } from '../../../mocks/appointments';

interface WidgetDailyAppointmentsProps {
  appointments: Appointment[];
  types: AppointmentType[];
  loading?: boolean;
  onEdit?: (appt: Appointment) => void;
}

const monthNames = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function formatDateNice(iso: string) {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} de ${monthNames[d.getMonth()]}`;
}

function isToday(iso: string) {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return iso === `${y}-${m}-${day}`;
}

function isTomorrow(iso: string) {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const day = String(t.getDate()).padStart(2, '0');
  return iso === `${y}-${m}-${day}`;
}

export default function WidgetDailyAppointments({
  appointments,
  types,
  loading,
  onEdit,
}: WidgetDailyAppointmentsProps) {
  const todayIso = useMemo(() => {
    const t = new Date();
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const day = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);

  const todayAppointments = useMemo(
    () =>
      appointments
        .filter((a) => a.preferred_date === todayIso && a.status !== 'cancelled')
        .sort((a, b) => (a.preferred_time || '').localeCompare(b.preferred_time || '')),
    [appointments, todayIso]
  );

  const upcomingAppointments = useMemo(
    () =>
      appointments
        .filter((a) => a.preferred_date > todayIso && a.status !== 'cancelled')
        .sort((a, b) => {
          const da = new Date(a.preferred_date + 'T' + (a.preferred_time || '00:00')).getTime();
          const db = new Date(b.preferred_date + 'T' + (b.preferred_time || '00:00')).getTime();
          return da - db;
        })
        .slice(0, 5),
    [appointments, todayIso]
  );

  const todayCount = todayAppointments.length;
  const upcomingCount = upcomingAppointments.length;
  const totalRevenueToday = todayAppointments.reduce((sum, a) => {
    const type = types.find((t) => t.name === a.service_type);
    return sum + (a.paid && type?.price ? type.price : 0);
  }, 0);

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center">
              <i className="ri-calendar-check-line text-brand-black text-lg" />
            </div>
            <div>
              <p className="font-sans text-brand-gray-400 text-xs uppercase tracking-wider">Hoy</p>
              <p className="font-serif text-brand-black text-2xl font-medium leading-none">{todayCount}</p>
            </div>
          </div>
          <p className="font-sans text-brand-gray-500 text-xs">{formatDateNice(todayIso)}</p>
        </div>

        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center">
              <i className="ri-calendar-event-line text-brand-black text-lg" />
            </div>
            <div>
              <p className="font-sans text-brand-gray-400 text-xs uppercase tracking-wider">Próximas</p>
              <p className="font-serif text-brand-black text-2xl font-medium leading-none">{upcomingCount}</p>
            </div>
          </div>
          <p className="font-sans text-brand-gray-500 text-xs">En los próximos días</p>
        </div>

        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <i className="ri-money-euro-circle-line text-emerald-700 text-lg" />
            </div>
            <div>
              <p className="font-sans text-brand-gray-400 text-xs uppercase tracking-wider">Ingresos hoy</p>
              <p className="font-serif text-brand-black text-2xl font-medium leading-none">
                {totalRevenueToday > 0 ? `€${totalRevenueToday.toFixed(2)}` : '—'}
              </p>
            </div>
          </div>
          <p className="font-sans text-brand-gray-500 text-xs">
            {todayAppointments.filter((a) => a.paid).length} pagadas
          </p>
        </div>
      </div>

      {/* Today's appointments */}
      <div>
        <h3 className="font-serif text-brand-black text-lg font-medium mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brand-sage" />
          Citas de hoy
          <span className="font-sans text-brand-gray-400 text-sm font-normal">({todayCount})</span>
        </h3>

        {loading ? (
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-10 text-center">
            <i className="ri-loader-4-line animate-spin text-brand-gray-400 text-xl" />
            <p className="font-sans text-brand-gray-400 text-sm mt-2">Cargando citas...</p>
          </div>
        ) : todayCount === 0 ? (
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-brand-gray-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-calendar-line text-brand-gray-400 text-xl" />
            </div>
            <p className="font-sans text-brand-gray-500 text-sm">No hay citas programadas para hoy</p>
            <p className="font-sans text-brand-gray-400 text-xs mt-1">Puedes crear una desde la pestaña Reservas</p>
          </div>
        ) : (
          <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-gray-50">
                  <tr>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Hora
                    </th>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Cliente
                    </th>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Servicio
                    </th>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Estado
                    </th>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Pago
                    </th>
                    <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-gray-200">
                  {todayAppointments.map((appt) => {
                    const st = statusLabels[appt.status];
                    const type = types.find((t) => t.name === appt.service_type);
                    const hasPrice = type?.price !== undefined && type.price > 0;

                    return (
                      <tr key={appt.id} className="hover:bg-brand-gray-50/50 transition-colors">
                        <td className="px-5 py-4">
                          <p className="font-sans text-brand-black text-sm font-medium">
                            {appt.preferred_time || '—'}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="font-sans text-brand-black text-sm font-medium">
                            {appt.name}
                          </p>
                          <p className="font-sans text-brand-gray-400 text-xs">{appt.email}</p>
                          {appt.phone && (
                            <p className="font-sans text-brand-gray-400 text-xs">{appt.phone}</p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <p className="font-sans text-brand-gray-600 text-sm">
                            {appt.service_type}
                          </p>
                          {type?.duration_minutes && (
                            <p className="font-sans text-brand-gray-400 text-xs">
                              {type.duration_minutes} min
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium ${st?.bg} ${st?.color}`}
                          >
                            {st?.label}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          {hasPrice ? (
                            appt.paid ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-emerald-50 text-emerald-700">
                                <i className="ri-checkbox-circle-line" />
                                Pagada
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-amber-50 text-amber-700">
                                <i className="ri-time-line" />
                                Pendiente
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-brand-gray-100 text-brand-gray-500">
                              Gratuita
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right">
                          {onEdit && (
                            <button
                              onClick={() => onEdit(appt)}
                              className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                              title="Editar"
                            >
                              <i className="ri-pencil-line" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Upcoming preview */}
      {upcomingCount > 0 && (
        <div>
          <h3 className="font-serif text-brand-black text-lg font-medium mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand-gray-300" />
            Próximas citas
            <span className="font-sans text-brand-gray-400 text-sm font-normal">({upcomingCount})</span>
          </h3>

          <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-gray-50">
                  <tr>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Fecha / Hora
                    </th>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Cliente
                    </th>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Servicio
                    </th>
                    <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                      Pago
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-gray-200">
                  {upcomingAppointments.map((appt) => {
                    const type = types.find((t) => t.name === appt.service_type);
                    const hasPrice = type?.price !== undefined && type.price > 0;
                    const isTmr = isTomorrow(appt.preferred_date);

                    return (
                      <tr key={appt.id} className="hover:bg-brand-gray-50/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            {isTmr && (
                              <span className="font-sans text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-sage/20 text-brand-black font-medium">
                                Mañana
                              </span>
                            )}
                            <div>
                              <p className="font-sans text-brand-black text-sm">
                                {formatDateNice(appt.preferred_date)}
                              </p>
                              <p className="font-sans text-brand-gray-400 text-xs">
                                {appt.preferred_time}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-sans text-brand-black text-sm">{appt.name}</p>
                          <p className="font-sans text-brand-gray-400 text-xs">{appt.email}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="font-sans text-brand-gray-600 text-sm">{appt.service_type}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          {hasPrice ? (
                            appt.paid ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-emerald-50 text-emerald-700">
                                <i className="ri-checkbox-circle-line" />
                                Pagada
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-amber-50 text-amber-700">
                                <i className="ri-time-line" />
                                Pendiente
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full font-sans text-[11px] font-medium bg-brand-gray-100 text-brand-gray-500">
                              Gratuita
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}